import React, { useCallback, useEffect, useMemo, useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  Stack,
  TextField
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { GroupSelectionValue, InvestorGroupAutocomplete } from "components/Autocompletes/InvestorGroupAutocomplete";
import { NumberInput } from "components/forms";
import { Dropdown } from "components/Tagger/General/Dropdown";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { currenciesArray } from "constants/index";
import dayjs, { Dayjs } from "dayjs";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions, useMeasureParametersSelector } from "store";
import { useAccountSelector, useDateFormatSelect } from "store/selectors/user-selectors";
import { IDataExtractionTag, IUpdateFileTagData } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

export interface EditParameterResult {
  measureId: number;
  parameterName: string;
  value: number | null;
  currency: string | null;
  effectiveDate: string | null;
  accountingDate: string | null;
  investorId: number | null;
  investorName: string | null;
  isGlobalValue: boolean;
}

type ValidationErrors = Record<string, string[]>;

interface Props {
  fileId: number;
  dealId: number | null;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  parameter: IDataExtractionTag;
  anchorEl: HTMLElement;
  onSubmit: (updatedTag: IDataExtractionTag) => void;
  onCancel: () => void;
}

type MeasureOption = { value: number; label: string; global: boolean };

export const EditParameterPrompt: React.FC<Props> = ({
  fileId,
  dealId,
  dealInvestors,
  parameter,
  anchorEl,
  onSubmit,
  onCancel
}) => {
  const { allow_split_by_group } = useAccountSelector() || {};
  const measureParameters = useMeasureParametersSelector();
  const dateFormat = useDateFormatSelect();

  const { updateFileTagAction, getDealRoundsWithInvestorsAction } = useDealActions();
  const { action: patchTag, loading: patching } = useActionHook(updateFileTagAction);
  const {
    action: fetchRounds,
    loading: roundsLoading,
    data: roundsData = []
  } = useActionHook(getDealRoundsWithInvestorsAction);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const anchorPosition = useMemo(() => {
    const rect = anchorEl.getBoundingClientRect();
    return { top: rect.bottom, left: rect.left };
  }, [anchorEl]);

  const measureOptions = useMemo<MeasureOption[]>(
    () => measureParameters.map((m) => ({ value: m.id, label: m.name, global: m.is_global_value })),
    [measureParameters]
  );

  const isMeasureGlobalValue = (measureId: number) => {
    return measureOptions.find((option) => option.value === measureId)?.global || false;
  };

  const initialMeasure = useMemo(
    () => measureOptions.find((m) => m.value === parameter.measure?.id || m.label === parameter.measure?.name) || null,
    [measureOptions, parameter.measure?.id, parameter.measure?.name]
  );

  const [selectedMeasure, setSelectedMeasure] = useState<MeasureOption | null>(initialMeasure);
  const [value, setValue] = useState<number | null>(parameter.value ?? null);
  const [currency, setCurrency] = useState<string | null>(parameter.currency);
  const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(
    parameter.effective_date ? dayjs(parameter.effective_date) : null
  );
  const [accountingDate, setAccountingDate] = useState<Dayjs | null>(
    parameter.accounting_date ? dayjs(parameter.accounting_date) : null
  );
  const [allInvestors, setAllInvestors] = useState(parameter.all_investors);
  const [selectedInvestor, setSelectedInvestor] = useState<{ id: number; name: string } | null>(
    parameter.investor || null
  );
  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(parameter.round?.id ?? null);

  const [selectedInvestorIds, setSelectedInvestorIds] = useState<number[]>(parameter.investor_ids ?? []);
  const [splitByEntireCommitment, setSplitByEntireCommitment] = useState(parameter.entire_commitment ?? false);

  useEffect(() => {
    if (!dealId) return;
    fetchRounds({ dealId });
  }, [dealId]);

  const roundOptions = useMemo(() => roundsData.map((r) => ({ value: r.round_id, label: r.round_name })), [roundsData]);

  const allInvestorsOption = useMemo(() => dealInvestors.find((inv) => inv.id < 0), [dealInvestors]);

  const isGlobalValue = selectedMeasure ? isMeasureGlobalValue(selectedMeasure.value) : false;
  const { is_manually_changed } = parameter;

  const getFieldError = (field: string): string | undefined => {
    return errors[field]?.join(" ");
  };

  const handleApply = async () => {
    if (!selectedMeasure) return;
    setErrors({});

    const result: EditParameterResult = {
      measureId: selectedMeasure.value,
      parameterName: selectedMeasure.label,
      value,
      currency,
      effectiveDate: effectiveDate?.isValid() ? toIsoMidday(effectiveDate) : null,
      accountingDate: accountingDate?.isValid() ? toIsoMidday(accountingDate) : null,
      investorId: isGlobalValue ? null : (selectedInvestor?.id ?? null),
      investorName: isGlobalValue ? null : (selectedInvestor?.name ?? null),
      isGlobalValue
    };

    try {
      const payload: IUpdateFileTagData = {};

      if (result.measureId !== parameter.measure?.id) {
        payload.measure_id = result.measureId;
      }
      if ((result.value ?? 1) !== parameter.value) {
        payload.value = result.value ?? 1;
      }
      if (result.currency !== parameter.currency) {
        if (result.currency) {
          payload.currency = result.currency;
        }
      }
      if ((result.accountingDate || null) !== (parameter.accounting_date || null)) {
        payload.accounting_date = result.accountingDate || null;
      }
      if ((result.effectiveDate || null) !== (parameter.effective_date || null)) {
        payload.effective_date = result.effectiveDate || null;
      }
      const investorChanged =
        allInvestors !== parameter.all_investors ||
        (!allInvestors && (result.investorId || null) !== (parameter.investor?.id || null));
      if (investorChanged) {
        payload.all_investors = allInvestors;
        payload.investor_id = allInvestors ? null : result.investorId || null;
      }
      if (!isGlobalValue && (selectedRoundId ?? null) !== (parameter.round?.id ?? null)) {
        payload.round_id = selectedRoundId;
      }
      if (selectedInvestorIds.length) {
        payload.investor_id = null;
        payload.investor_ids = selectedInvestorIds;
        payload.all_investors = false;
        payload.group_commitment = !splitByEntireCommitment;
        payload.entire_commitment = splitByEntireCommitment;
      } else {
        payload.investor_ids = [];
        payload.group_commitment = false;
        payload.entire_commitment = false;
      }

      const updatedTag = await patchTag(fileId, parameter.id, payload);
      onSubmit(updatedTag);
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "errors" in err &&
        typeof (err as Record<string, unknown>).errors === "object"
      ) {
        setErrors((err as Record<string, ValidationErrors>).errors);
      }
    }
  };

  const handleGroupsChange = useCallback(
    (value: GroupSelectionValue) => {
      setSelectedInvestorIds(value.investorsIds);

      if (value.groupsName.length) {
        setSelectedInvestor(null);
        setAllInvestors(false);
      } else if (value.investorId !== null) {
        const isAll = value.investorId === allInvestorsOption?.id;
        setAllInvestors(isAll);
        setSelectedInvestor(
          isAll
            ? null
            : { id: value.investorId, name: dealInvestors.find((d) => d.id === value.investorId)?.name || "" }
        );
        setSplitByEntireCommitment(false);
        setSelectedInvestorIds([]);
      }
    },
    [dealInvestors, allInvestorsOption]
  );

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onCancel}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "8px",
            boxShadow: "-2px 2px 15px 0px rgba(0,0,0,0.25)",
            width: 379,
            p: "24px"
          }
        }
      }}
    >
      <Stack gap="16px">
        <Dropdown
          label="Parameter"
          placeholder="Choose Parameter"
          options={measureOptions}
          value={selectedMeasure}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }}
          onChange={(_, option) => {
            if (!option) return;
            setSelectedMeasure(option);
            if (isMeasureGlobalValue(option.value)) {
              setSelectedInvestor(null);
              setSelectedRoundId(null);
              setAllInvestors(false);
            } else {
              if (roundOptions.length === 1) {
                setSelectedRoundId(roundOptions[0].value);
              }
            }
          }}
        />

        <Stack direction="row" gap="16px">
          <Stack direction="row" alignItems="center" gap="4px" sx={{ flex: 1, minWidth: 0 }}>
            <NumberInput
              size="small"
              placeholder="Enter value"
              label="Value"
              fullWidth
              variant="outlined"
              value={value}
              onChange={(e) => setValue(e.target.value ? Number(e.target.value) : null)}
              error={!!errors.value}
              helperText={getFieldError("value")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }}
            />
            {is_manually_changed.value && (
              <MaterialTooltip isShow title="Value differs from the original PDF settings">
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
              </MaterialTooltip>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap="4px" sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              size="small"
              multiple={false}
              fullWidth
              disableClearable
              value={currency || undefined}
              options={currenciesArray}
              onChange={(_, option) => setCurrency(option)}
              getOptionLabel={(option) => option ?? ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Currency"
                  placeholder="Choose Currency"
                  variant="outlined"
                  error={!!errors.currency}
                  helperText={getFieldError("currency")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }}
                />
              )}
            />
            {is_manually_changed.currency && (
              <MaterialTooltip isShow title="Currency differs from the original PDF settings">
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
              </MaterialTooltip>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap="4px">
          <DesktopDatePicker
            label="Effective Date"
            format={dateFormat}
            views={["year", "month", "day"]}
            value={effectiveDate}
            onChange={(date) => {
              if (date && date.isValid()) {
                setEffectiveDate(date);
              }
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                fullWidth: true,
                error: !!errors.effective_date,
                helperText: getFieldError("effective_date"),
                sx: { "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }
              }
            }}
          />
          {is_manually_changed.effective_date && (
            <MaterialTooltip isShow title="Effective Date differs from the original PDF settings">
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
            </MaterialTooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap="4px">
          <DesktopDatePicker
            label="Accounting Date"
            format={dateFormat}
            views={["year", "month", "day"]}
            value={accountingDate}
            onChange={(date) => {
              if (date && date.isValid()) {
                setAccountingDate(date);
              }
            }}
            slotProps={{
              inputAdornment: { position: "start" },
              popper: {
                sx: {
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                fullWidth: true,
                error: !!errors.accounting_date,
                helperText: getFieldError("accounting_date"),
                sx: { "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }
              }
            }}
          />
          {is_manually_changed.accounting_date && (
            <MaterialTooltip isShow title="Accounting Date differs from the original PDF settings">
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
            </MaterialTooltip>
          )}
        </Stack>
        {roundOptions.length > 1 && (
          <Autocomplete
            size="small"
            fullWidth
            disabled={!selectedMeasure || isGlobalValue}
            loading={roundsLoading}
            options={roundOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            value={roundOptions.find((opt) => opt.value === selectedRoundId) ?? null}
            onChange={(_, option) => {
              setSelectedInvestor(null);
              setAllInvestors(false);
              setSelectedRoundId(option?.value || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Round"
                placeholder="Select Round"
                error={!!errors.round_id}
                helperText={getFieldError("round_id")}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#7b92a9" } } }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        <Stack direction="row" alignItems="center" gap="4px">
          {allow_split_by_group ? (
            <InvestorGroupAutocomplete
              dealInvestors={dealInvestors}
              preselectedInvestorId={
                isGlobalValue ? null : allInvestors ? (allInvestorsOption?.id ?? null) : (selectedInvestor?.id ?? null)
              }
              allInvestorsId={allInvestorsOption?.id}
              showHint
              onChange={(inv) => {
                setSelectedInvestor(inv);
                setAllInvestors(false);
              }}
              onGroupsChange={handleGroupsChange}
              preselectedInvestorIds={selectedInvestorIds}
              disabled={isGlobalValue}
              label="Investor"
              error={!!errors.investor_id}
              helperText={getFieldError("investor_id")}
              sx={{
                ...(isGlobalValue && { pointerEvents: "none", opacity: 0.5 }),
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "#7b92a9" }
                }
              }}
            />
          ) : (
            <InvestorAutocomplete
              dealInvestors={dealInvestors}
              preselectedInvestorId={
                isGlobalValue ? null : allInvestors ? (allInvestorsOption?.id ?? null) : (selectedInvestor?.id ?? null)
              }
              onChange={(inv) => {
                const isAll = inv?.id === allInvestorsOption?.id;
                setAllInvestors(isAll);
                setSelectedInvestor(isAll ? null : inv);
              }}
              disabled={isGlobalValue}
              label="Investor"
              error={!!errors.investor_id}
              helperText={getFieldError("investor_id")}
              sx={{
                ...(isGlobalValue && { pointerEvents: "none", opacity: 0.5 }),
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": { borderColor: "#7b92a9" }
                }
              }}
            />
          )}

          {is_manually_changed.investor_id && (
            <MaterialTooltip isShow title="Investor differs from the original PDF settings">
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#EBAF01" }} />
            </MaterialTooltip>
          )}
        </Stack>
        {Boolean(selectedInvestorIds.length) && (
          <MaterialTooltip
            title="Uses this group's total commitment as a share of the deal total to derive values for all non-group investors."
            isShow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={splitByEntireCommitment}
                  onChange={(_, checked) => setSplitByEntireCommitment(checked)}
                  size="small"
                />
              }
              label="Derive values for remaining investors."
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#071f35" } }}
            />
          </MaterialTooltip>
        )}

        <Stack direction="row" justifyContent="flex-end" gap="8px">
          <Button
            onClick={onCancel}
            sx={{
              borderRadius: "32px",
              border: "1px solid #7b92a9",
              color: "#071f35",
              textTransform: "none",
              padding: "4px 16px",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px"
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedMeasure || patching}
            sx={{
              borderRadius: "32px",
              bgcolor: "#071f35",
              color: "white",
              textTransform: "none",
              padding: "4px 16px",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              "&:hover": { bgcolor: "#0a2a45" },
              "&:disabled": { bgcolor: "#bac7ce", color: "white" }
            }}
          >
            {patching ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Apply"}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
